import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import ReplayCard from "@/components/base/ReplayCard";
import { replays } from "@/mocks/replays";
import { leagues } from "@/mocks/leagues";
import { usePageMeta } from "@/hooks/usePageMeta";

const TODAY = "2026-10-15";

const dateFilters = [
  { key: "all", label: "全部日期" },
  { key: "today", label: "今日" },
  { key: "yesterday", label: "昨日" },
  { key: "earlier", label: "更早" },
] as const;

type DateKey = (typeof dateFilters)[number]["key"];

export default function Replay() {
  const [leagueKey, setLeagueKey] = useState("all");
  const [dateKey, setDateKey] = useState<DateKey>("all");
  const [visible, setVisible] = useState(8);

  usePageMeta(
    "足球赛事回放视频 - 51网站在线观看免费播播足球",
    "51足球直播回放专区，提供英超、西甲、欧冠、世界杯等足球赛事全场回放与精彩集锦，随时回看精彩比赛视频。",
  );

  const filtered = replays.filter((r) => {
    if (leagueKey !== "all" && r.leagueKey !== leagueKey) return false;
    if (dateKey === "today") return r.date === TODAY;
    if (dateKey === "yesterday") return r.date === "2026-10-14";
    if (dateKey === "earlier") return r.date < "2026-10-14";
    return true;
  });
  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setLeagueKey("all");
    setDateKey("all");
    setVisible(8);
  };

  return (
    <SiteLayout>
      <PageHeader
        title="精彩回放"
        subtitle="错过直播没关系，这里有全场回放与精彩集锦，按联赛和日期快速找到你想看的比赛。"
        crumbs={[{ label: "首页", to: "/" }, { label: "精彩回放" }]}
        aside={
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 text-white text-sm backdrop-blur">
            <i className="ri-film-line text-accent-400" />
            共 {replays.length} 个回放视频
          </div>
        }
      />

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto">
          <div className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5 mb-7 flex flex-col gap-4">
            <div>
              <p className="text-xs text-foreground-400 mb-2">按联赛筛选</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setLeagueKey("all")}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    leagueKey === "all"
                      ? "bg-primary-500 text-white"
                      : "bg-background-50 text-foreground-700 hover:bg-primary-50"
                  }`}
                >
                  全部
                </button>
                {leagues.map((lg) => (
                  <button
                    key={lg.key}
                    type="button"
                    onClick={() => {
                      setLeagueKey(lg.key);
                      setVisible(8);
                    }}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      leagueKey === lg.key
                        ? "bg-primary-500 text-white"
                        : "bg-background-50 text-foreground-700 hover:bg-primary-50"
                    }`}
                  >
                    {lg.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs text-foreground-400">按日期筛选</p>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-50">
                {dateFilters.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => {
                      setDateKey(d.key);
                      setVisible(8);
                    }}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      dateKey === d.key
                        ? "bg-accent-500 text-white"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="py-16 text-center bg-background-100 rounded-lg border border-background-200">
              <i className="ri-video-off-line text-4xl text-foreground-300" />
              <p className="mt-3 text-sm text-foreground-500">
                暂无符合条件的回放视频
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" />
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {shown.map((r) => (
                  <ReplayCard key={r.id} replay={r} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + 8)}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-background-50 border border-background-200 hover:bg-primary-50 hover:border-primary-300 text-foreground-800 text-sm font-medium transition-colors cursor-pointer"
                  >
                    加载更多回放
                    <i className="ri-arrow-down-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}